import {
    GET_STARTED_DRAFT_SELECTED_PAIRS,
    GET_STARTED_DRAFT_SELECTED_SPEAKER_IDS,
} from '../constants/local_storage_constants';

export const getStartedPairKey = ({ speakerId, topic }) => JSON.stringify([topic, speakerId]);

export const normalizeSelectedPairs = (pairs = []) => {
    const seen = new Set();
    return (Array.isArray(pairs) ? pairs : []).reduce((result, pair) => {
        const speakerId = typeof pair?.speakerId === 'string' ? pair.speakerId.trim() : '';
        const topic = typeof pair?.topic === 'string' ? pair.topic.trim() : '';
        if (!speakerId || !topic) return result;
        const normalized = { speakerId, topic };
        const key = getStartedPairKey(normalized);
        if (!seen.has(key)) {
            seen.add(key);
            result.push(normalized);
        }
        return result;
    }, []);
};

export const readGetStartedDraft = () => {
    try {
        const pairDraft = localStorage.getItem(GET_STARTED_DRAFT_SELECTED_PAIRS);
        if (pairDraft) {
            const draft = JSON.parse(pairDraft);
            if (Array.isArray(draft?.selectedPairs)) return {
                rosterVersion: typeof draft.rosterVersion === 'string' ? draft.rosterVersion : null,
                selectedPairs: normalizeSelectedPairs(draft.selectedPairs),
            };
            return { selectedPairs: [] };
        }
        const legacy = JSON.parse(localStorage.getItem(GET_STARTED_DRAFT_SELECTED_SPEAKER_IDS) || '[]');
        return { legacySpeakerIds: Array.isArray(legacy) ? legacy : [], selectedPairs: [] };
    } catch {
        return { selectedPairs: [] };
    }
};

// Preserve the listener's exact choices across roster updates. A draft never
// chooses a different person or guesses a category for somebody in multiple rows.
export const reconcileGetStartedDraft = (draft = {}, roster = {}) => {
    const rosterVersion = roster.rosterVersion || null;
    const knownPairs = (roster.data || []).flatMap(row => row.speakers.map(speaker => ({
        speakerId: speaker.id, topic: row.topic,
    })));
    const knownKeys = new Set(knownPairs.map(getStartedPairKey));
    if (Array.isArray(draft.legacySpeakerIds)) {
        const selectedPairs = [];
        const legacyIds = [...new Set(draft.legacySpeakerIds.filter(id => typeof id === 'string'))];
        for (const id of legacyIds) {
            const matches = knownPairs.filter(pair => pair.speakerId === id.trim());
            if (matches.length === 1) selectedPairs.push(matches[0]);
        }
        return { rosterVersion, selectedPairs, picksChanged: selectedPairs.length < legacyIds.length };
    }
    const savedPairs = normalizeSelectedPairs(draft.selectedPairs);
    const selectedPairs = savedPairs.filter(pair => knownKeys.has(getStartedPairKey(pair)));
    return { rosterVersion, selectedPairs, picksChanged: selectedPairs.length < savedPairs.length };
};

export const persistGetStartedDraft = ({ rosterVersion, selectedPairs }) => {
    try {
        if (selectedPairs.length) {
            localStorage.setItem(GET_STARTED_DRAFT_SELECTED_PAIRS, JSON.stringify({ rosterVersion, selectedPairs }));
            // Keep the legacy value for older clients, while new clients prefer
            // the pair-aware draft above.
            localStorage.setItem(GET_STARTED_DRAFT_SELECTED_SPEAKER_IDS, JSON.stringify(
                [...new Set(selectedPairs.map(pair => pair.speakerId))],
            ));
        } else {
            clearGetStartedDraft();
        }
    } catch {
        // The current picker still works when browser storage is unavailable.
    }
};

export const clearGetStartedDraft = () => {
    localStorage.removeItem(GET_STARTED_DRAFT_SELECTED_PAIRS);
    localStorage.removeItem(GET_STARTED_DRAFT_SELECTED_SPEAKER_IDS);
};

export const getStartedSelectionPayload = (roster, pairs) => {
    const selectedPairs = normalizeSelectedPairs(pairs);
    const selectedSpeakerIds = [...new Set(selectedPairs.map(pair => pair.speakerId))];
    const selectedTopicSet = new Set(selectedPairs.map(pair => pair.topic));
    const rows = roster.data || [];
    const selectedTopics = rows.filter(row => selectedTopicSet.has(row.topic)).map(row => row.topic);
    return {
        selectedTopics,
        // An unchecked card is not a dislike. Retain these legacy fields for
        // older servers without turning browsing order into negative interests.
        unselectedTopics: [],
        selectedSpeakerIds,
        unselectedSpeakerIds: [],
        ...(roster.rosterVersion ? { rosterVersion: roster.rosterVersion, selectedPairs } : {}),
    };
};
